import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtUSD, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Download, FileText, Wallet, TrendingUp, TrendingDown, Mail, ArrowDownLeft, ArrowUpRight } from "lucide-react";

export default function Statements() {
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    api.get("/portal/account").then(({ data }) => setData(data)).catch(() => {});
    api.get("/portal/transactions").then(({ data }) => setTxns(data)).catch(() => setTxns({ transactions: [] }));
  }, []);

  const sendEmail = async () => {
    if (!emailTo.trim()) { toast.error("Please enter an email address."); return; }
    setEmailSending(true);
    try {
      const { data } = await api.post(`/portal/statement/${emailFor.statement_id}/email`, { to_email: emailTo.trim() });
      if (data.sent) toast.success(`Statement emailed to ${data.to_email}`);
      else if (!data.configured) toast.info("Email delivery isn't switched on yet. Ask your team to enable it (SendGrid).");
      else toast.error("Couldn't send the email. Please try again.");
      setEmailFor(null);
      setEmailTo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the email.");
    } finally { setEmailSending(false); }
  };

  const download = async (stmt, format) => {
    const key = `${stmt.statement_id}-${format}`;
    setDownloading(key);
    try {
      const isPdf = format === "pdf";
      const path = isPdf ? `/portal/statement/${stmt.statement_id}/pdf` : `/portal/statement/${stmt.statement_id}`;
      const res = await api.get(path, { responseType: "blob" });
      const mime = isPdf ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `MentorAI_Statement_${stmt.period_key}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${stmt.period} statement (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Could not download statement.");
    } finally { setDownloading(null); }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><div className="grid sm:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>;

  const acc = data.account;
  const pnl = acc.equity - acc.initial_deposit;
  const up = pnl >= 0;

  return (
    <div className="space-y-6" data-testid="statements-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Statements</h1>
        <p className="text-sm text-muted-foreground mt-1">Account {acc.account_no} · <span className="text-amber-600">simulated</span>. Download your monthly statements below.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="rounded-xl p-5 ring-1 ring-border">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Wallet className="size-4" /> Current value</div>
          <div className="mt-2 font-display text-2xl font-semibold font-mono-num">{fmtUSD(acc.equity)}</div>
        </Card>
        <Card className="rounded-xl p-5 ring-1 ring-border">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">{up ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />} Total growth</div>
          <div className={`mt-2 font-display text-2xl font-semibold font-mono-num ${up ? "text-emerald-600" : "text-red-600"}`}>{up ? "+" : ""}{fmtUSD(pnl)}</div>
        </Card>
      </div>

      <Card className="rounded-xl ring-1 ring-border overflow-hidden" data-testid="transactions-card">
        <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-medium">Contributions & withdrawals</h2>
          {txns && (
            <div className="flex items-center gap-4 text-xs">
              <span className="text-muted-foreground">Deposits <span className="font-mono-num text-emerald-600">{fmtUSD(txns.total_deposits)}</span></span>
              <span className="text-muted-foreground">Withdrawals <span className="font-mono-num text-red-600">{fmtUSD(txns.total_withdrawals)}</span></span>
              <span className="text-muted-foreground">Net <span className="font-mono-num text-foreground">{fmtUSD(txns.net_contributions)}</span></span>
            </div>
          )}
        </div>
        {!txns ? (
          <div className="p-4 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : txns.transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No transactions yet.</div>
        ) : (
          <div className="divide-y">
            {txns.transactions.map((t) => {
              const isDep = t.type === "deposit";
              return (
                <div key={t.tx_id} className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className={`size-9 rounded-lg flex items-center justify-center ${isDep ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"}`}>
                      {isDep ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
                    </div>
                    <div>
                      <div className="font-medium text-sm capitalize">{t.type}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(t.date)} · {t.method}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono-num text-sm ${isDep ? "text-emerald-600" : "text-red-600"}`}>{isDep ? "+" : "−"}{fmtUSD(t.amount)}</div>
                    <div className="text-[11px] text-muted-foreground">{t.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="rounded-xl ring-1 ring-border overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="font-medium">Monthly statements</h2>
          <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 text-[11px]">SIMULATED</Badge>
        </div>
        <div className="divide-y">
          {data.statements.map((st) => (
            <div key={st.statement_id} className="flex items-center justify-between px-5 py-4 hover:bg-muted gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-lg bg-brand/10 flex items-center justify-center text-brand"><FileText className="size-4" /></div>
                <div>
                  <div className="font-medium text-sm">{st.period} statement</div>
                  <div className="text-xs text-muted-foreground">Generated {fmtDate(st.generated_at)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="portal-download-pdf-button" size="sm" className="rounded-lg gap-1.5 bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white" disabled={downloading === `${st.statement_id}-pdf`} onClick={() => download(st, "pdf")}>
                  <FileText className="size-4" /> {downloading === `${st.statement_id}-pdf` ? "…" : "PDF"}
                </Button>
                <Button data-testid="portal-download-csv-button" variant="outline" size="sm" className="rounded-lg gap-1.5" disabled={downloading === `${st.statement_id}-csv`} onClick={() => download(st, "csv")}>
                  <Download className="size-4" /> {downloading === `${st.statement_id}-csv` ? "…" : "CSV"}
                </Button>
                <Button data-testid="portal-email-statement-button" variant="outline" size="sm" className="rounded-lg gap-1.5" onClick={() => { setEmailFor(st); setEmailTo(""); }}>
                  <Mail className="size-4" /> Email
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor(null)}>
        <DialogContent data-testid="email-statement-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email statement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Send the {emailFor?.period} PDF statement to yourself or your advisor.</p>
            <div className="space-y-2">
              <Label>Recipient email</Label>
              <Input data-testid="email-statement-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="advisor@firm.com" onKeyDown={(e) => e.key === "Enter" && sendEmail()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setEmailFor(null)}>Cancel</Button>
            <Button data-testid="email-statement-send" className="rounded-lg bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white gap-1.5" disabled={emailSending} onClick={sendEmail}>
              <Mail className="size-4" /> {emailSending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
